import React, { useState, useEffect} from 'react';
import { doc, updateDoc, collection, query, where, getDocs, deleteDoc, getDoc } from 'firebase/firestore';
import ClientWeekView from './ClientWeekView';
import ClientMonthView from './ClientMonthView';
import ClientContract from './ClientContract';
import ClientReport from './ClientReport';
import AppointmentModal from './AppointmentModal';
import ClientSummary from './ClientSummary';
import ClientDiscussion from './ClientDiscussion';


// Fonctions utilitaires
const adjustForTimezone = (date) => {
  return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
};

const colorPalette = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F06292', '#AED581',
  '#FFD54F', '#7986CB', '#9575CD', '#4DB6AC', '#FFF176', '#FF8A65', '#A1887F',
  '#90A4AE', '#FFAB91', '#F48FB1', '#81C784', '#64B5F6', '#BA68C8', '#4DD0E1',
  '#DCE775', '#FFB74D', '#E57373', '#F06292', '#BA68C8', '#9575CD', '#7986CB',
  '#64B5F6', '#4FC3F7', '#4DD0E1', '#4DB6AC', '#81C784', '#AED581', '#DCE775',
  '#FFF176', '#FFD54F', '#FFB74D', '#FF8A65', '#A1887F', '#90A4AE', '#D1C4E9',
  '#B39DDB', '#9575CD', '#7E57C2', '#673AB7', '#5C6BC0', '#3F51B5', '#3949AB',
  '#303F9F', '#283593', '#1A237E', '#1976D2', '#1565C0', '#0D47A1', '#0277BD',
  '#01579B', '#00838F', '#006064', '#004D40', '#00695C', '#004C3F', '#00BFA5',
  '#00ACC1', '#0097A7', '#00838F', '#006064'
];



const isLightColor = (color) => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return brightness > 155;
};

const ClientDetails = ({
  client,
  onClose,
  db,
  generalSettings,
  planningManagementSettings,
  currentUser,
  employeeAppointments,
  addNotification,
  hasPermission
}) => {
  const [clientData] = useState(client);
  const [activeTab, setActiveTab] = useState('summary');
  const [viewType, setViewType] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState({});
  const [employees, setEmployees] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [accessibleEmployees, setAccessibleEmployees] = useState([]);
  const [, setGoogleMapsLoaded] = useState(false);
 
  // Charger Google Maps API
  useEffect(() => {
    const checkGoogleMapsLoaded = () => {
      return !!(window.google && window.google.maps && typeof window.google.maps.Geocoder === 'function');
    };

    if (checkGoogleMapsLoaded()) {
      setGoogleMapsLoaded(true);
      return;
    }

    // Vérifier si l'API est déjà en cours de chargement
    if (document.querySelector('script[data-gmaps]')) {
      const interval = setInterval(() => {
        if (checkGoogleMapsLoaded()) {
          clearInterval(interval);
          setGoogleMapsLoaded(true);
        }
      }, 100);
      return;
    }

    // Charger l'API seulement si la clé est configurée
    const loadGoogleMaps = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, 'settings', 'general'));
        if (settingsDoc.exists() && settingsDoc.data().googleMapsApiKey) {
          const script = document.createElement('script');
          script.setAttribute('data-gmaps', '1');
          script.src = `https://maps.googleapis.com/maps/api/js?key=${settingsDoc.data().googleMapsApiKey}&libraries=places,geocoding&v=weekly`;
          script.async = true;
          script.defer = true;
          script.onload = () => setGoogleMapsLoaded(true);
          document.head.appendChild(script);
        }
      } catch (error) {
        console.error('Erreur lors du chargement de la clé API Google Maps:', error);
      }
    };

    loadGoogleMaps();
  }, [db]);


  useEffect(() => {
    fetchAppointments();
    fetchEmployees();
  }, [client]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    console.log("selectedAppointment mis à jour:", selectedAppointment);
  }, [selectedAppointment]);

  const fetchAppointments = async () => {
    const appointmentsRef = collection(db, 'appointments');
    const q = query(appointmentsRef, where('clientId', '==', client.id));
    const querySnapshot = await getDocs(q);
    const fetchedAppointments = {};
    querySnapshot.forEach((doc) => {
      const appointment = doc.data();
      const dateString = appointment.date.toDate().toISOString().split('T')[0];
      if (!fetchedAppointments[dateString]) {
        fetchedAppointments[dateString] = [];
      }
      fetchedAppointments[dateString].push({ id: doc.id, ...appointment });
    });
    setAppointments(fetchedAppointments);
  };


  // Géocodage adresse → coords avec validation robuste
  const getCoordinatesFromAddress = async (address) => {
    // Validation robuste de l'adresse
    if (!address) {
      console.warn('❌ Adresse vide ou non définie');
      return null;
    }
    
    // Vérifier que l'adresse est une chaîne de caractères
    if (typeof address !== 'string') {
      console.warn('❌ Adresse n\'est pas une chaîne de caractères:', typeof address, address);
      return null;
    }
    
    // Nettoyer l'adresse
    const cleanAddress = address.trim();
    if (cleanAddress.length === 0) {
      console.warn('❌ Adresse vide après nettoyage');
      return null;
    }
    
    // Vérifier que l'adresse contient au moins un caractère alphanumérique
    if (!/[a-zA-Z0-9]/.test(cleanAddress)) {
      console.warn('❌ Adresse ne contient pas de caractères alphanumériques:', cleanAddress);
      return null;
    }

    if (!window.google || !window.google.maps || typeof window.google.maps.Geocoder !== 'function') {
      console.warn('Google Maps Geocoder non disponible');
      return null;
    }
    
    try {
      const geocoder = new window.google.maps.Geocoder();
      return new Promise((resolve, reject) => {
        geocoder.geocode({ address: cleanAddress }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const loc = results[0].geometry.location;
            console.log(`📍 Géocodage réussi pour: "${cleanAddress}"`);
            resolve({ lat: loc.lat(), lng: loc.lng() });
          } else {
            console.warn(`❌ Géocodage échoué pour: "${cleanAddress}" - Status: ${status}`);
            reject(new Error(`Adresse non trouvée: ${status}`));
          }
        });
      });
    } catch (e) {
      console.error('Erreur géocodage:', e);
      return null;
    }
  };

  const fetchEmployees = async () => {
    const employeesRef = collection(db, 'employees');
    const querySnapshot = await getDocs(employeesRef);
    const fetchedEmployees = querySnapshot.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data(),
      defaultRadius: doc.data().defaultRadius || 10 // Rayon par défaut 10km
    }));
    setEmployees(fetchedEmployees);
  };

  // Fonction pour calculer la distance entre deux coordonnées (formule Haversine)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Rayon de la Terre en km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI/180) * Math.cos(lat2 * Math.PI/180) * 
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c; // Distance en km
  };

  // Calculer les employés accessibles quand les données sont chargées
  useEffect(() => {
    const calculateAccessibleEmployees = async () => {
      if (!client?.address || employees.length === 0) return;

      try {
        // Géocoder l'adresse du client
        const clientCoords = await getCoordinatesFromAddress(client.address);
        if (!clientCoords) return;

        const accessible = [];

        for (const employee of employees) {
          if (!employee.address?.street) continue;
          
          // Construire l'adresse complète de l'employé
          const employeeAddress = `${employee.address.street}, ${employee.address.postalCode} ${employee.address.city}`;
          
          // Géocoder l'adresse de l'employé
          const employeeCoords = await getCoordinatesFromAddress(employeeAddress);
          if (!employeeCoords) continue;

          // Calculer la distance
          const distance = calculateDistance(
            employeeCoords.lat,
            employeeCoords.lng,
            clientCoords.lat,
            clientCoords.lng
          );

          // Vérifier si le client est dans la zone de l'employé
          const employeeRadius = employee.defaultRadius || 10;
          if (distance <= employeeRadius) {
            accessible.push({
              ...employee,
              distance: Math.round(distance * 10) / 10, // Arrondir à 1 décimale
              inZone: true
            });
          }
        }

        setAccessibleEmployees(accessible);
      } catch (error) {
        console.error("Erreur lors du calcul des employés accessibles:", error);
      }
    };

    calculateAccessibleEmployees();
  }, [client, employees]);

  const handleAddAppointment = (date) => {
    const newDate = new Date(date);
    newDate.setHours(12, 0, 0, 0);
  };

  const onUpdateAppointment = async (updatedAppointment) => {
    try {
      const appointmentRef = doc(db, 'appointments', updatedAppointment.id);
      await updateDoc(appointmentRef, updatedAppointment);
      console.log('Appointment updated: ', updatedAppointment.id);
      await fetchAppointments();
    } catch (error) {
      console.error('Error updating appointment: ', error);
    }
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('fr-FR', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'long' 
    }).format(date);
  };

  const formatTime = (time) => {
    return new Intl.DateTimeFormat('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(`2000-01-01T${time}`));
  };

  const changeDate = (amount, view) => {
    const newDate = new Date(currentDate);
    if (view === 'week') {
      newDate.setDate(newDate.getDate() + amount * 7);
    } else {
      newDate.setMonth(newDate.getMonth() + amount);
    }
    setCurrentDate(newDate);
  };

  const handleAppointmentClick = (appointment) => {
    setSelectedAppointment(appointment);
    setIsModalOpen(true);
  };
  
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAppointment(null);
  };
  
  const handleDeleteAppointment = async (appointmentId) => {
    try {
      await deleteDoc(doc(db, "appointments", appointmentId));
      setIsModalOpen(false);
      setSelectedAppointment(null);
      await fetchAppointments(); // Rafraîchir la liste des rendez-vous
    } catch (error) {
      console.error("Erreur lors de la suppression du rendez-vous:", error);
    }
  };

  const getStartOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  };

  return (
    <div className="flex h-full bg-gray-100">
      <div className="w-64 bg-white p-4 border-r">
        <button onClick={onClose} className="mb-4 text-teal-600 hover:text-teal-800">
          &lt; Retour
        </button>
        <div className="text-center mb-4">
          <div className="w-16 h-16 bg-teal-100 rounded-full mx-auto mb-2 flex items-center justify-center">
            <span className="text-teal-600 font-bold">{clientData.firstName?.charAt(0)}{clientData.lastName?.charAt(0)}</span>
          </div>
          <h2 className="text-xl font-bold text-teal-700">{clientData.firstName} {clientData.lastName}</h2>
          <p className="text-gray-600">{clientData.addressNumber}</p>
        </div>
          <nav>
            <button 
              onClick={() => setActiveTab('summary')}
              className={`w-full text-left py-2 px-4 rounded ${activeTab === 'summary' ? 'bg-teal-100 text-teal-700' : 'hover:bg-gray-100'}`}
            >
              Sommaire
            </button>
            <button 
              onClick={() => setActiveTab('discussion')}
              className={`w-full text-left py-2 px-4 rounded ${activeTab === 'discussion' ? 'bg-teal-100 text-teal-700' : 'hover:bg-gray-100'}`}
            >
              Discussion
            </button>
            <button 
              onClick={() => setActiveTab('notes')}
              className={`w-full text-left py-2 px-4 rounded ${activeTab === 'notes' ? 'bg-teal-100 text-teal-700' : 'hover:bg-gray-100'}`}
            >
              Notes
            </button>
            <button 
              onClick={() => setActiveTab('contrat')}
              className={`w-full text-left py-2 px-4 rounded ${activeTab === 'contrat' ? 'bg-teal-100 text-teal-700' : 'hover:bg-gray-100'}`}
            >
              Contrat
            </button>
            <button 
              onClick={() => setActiveTab('rapport')}
              className={`w-full text-left py-2 px-4 rounded ${activeTab === 'rapport' ? 'bg-teal-100 text-teal-700' : 'hover:bg-gray-100'}`}
            >
              Rapport
            </button>
            <button 
              onClick={() => setActiveTab('planning')}
              className={`w-full text-left py-2 px-4 rounded ${activeTab === 'planning' ? 'bg-teal-100 text-teal-700' : 'hover:bg-gray-100'}`}
            >
              Planning
            </button>
            <button 
              onClick={() => setActiveTab('collaborateurs')}
              className={`w-full text-left py-2 px-4 rounded ${activeTab === 'collaborateurs' ? 'bg-teal-100 text-teal-700' : 'hover:bg-gray-100'}`}
            >
              Collaborateurs
            </button>
            </nav>
            
      </div>
      <div className="flex-1 p-6 overflow-auto">
          {activeTab === 'summary' && (
            <ClientSummary client={clientData} db={db} />
          )}
          {activeTab === 'discussion' && (
          <div className="h-full">
            <h2 className="text-2xl font-bold mb-4">Discussion</h2>
            <ClientDiscussion 
              db={db}
              client={clientData}
              currentUser={currentUser}
              employeeAppointments={employeeAppointments}
              addNotification={addNotification}
            
            />
          </div>
        )}
          {activeTab === 'notes' && (
            <div>
              <h2 className="text-2xl font-bold mb-4">Notes</h2>
              {/* Implémenter la fonctionnalité de notes ici */}
            </div>
          )}
          {activeTab === 'contrat' && (
              <ClientContract client={clientData} db={db} />
            )}
            {activeTab === 'rapport' && (
              <ClientReport client={clientData} db={db} />
            )}
          {activeTab === 'planning' && (
        <div>
          <h2 className="text-2xl font-bold mb-4">Planning</h2>
          <div className="flex justify-between items-center mb-4">
          
            <select 
              value={viewType} 
              onChange={(e) => setViewType(e.target.value)}
              className="bg-white border rounded px-5 py-1"
            >
              <option value="week">Semaine</option>
              <option value="month">Mois</option>
            </select>
            
          </div>
          {viewType === 'week' ? (
            <ClientWeekView
              db={db}
              startOfWeek={getStartOfWeek(currentDate)}
              client={clientData}
              appointments={appointments}
              employees={employees}
              generalSettings={generalSettings}
              formatDate={formatDate}
              formatTime={formatTime}
              onAppointmentClick={handleAppointmentClick}
              changeDate={(amount) => changeDate(amount, 'week')}
              isLightColor={isLightColor}
              onAddAppointment={handleAddAppointment}
              onUpdateAppointment={onUpdateAppointment}
              onDeleteAppointment={handleDeleteAppointment}
              onHandleAddAppointment={handleAddAppointment}
              adjustForTimezone={adjustForTimezone}
              hasPermission={hasPermission}
            />
          ) : (
            <ClientMonthView
            db={db}
            client={clientData}
            currentDate={currentDate}
            appointments={appointments}
            employees={employees}
            generalSettings={generalSettings}
            planningManagementSettings={planningManagementSettings} // Ajout de cette ligne
            formatDate={formatDate}
            formatTime={formatTime}
            onAppointmentClick={handleAppointmentClick}
            changeDate={(amount) => changeDate(amount, 'month')}
            isLightColor={isLightColor}
            onAddAppointment={handleAddAppointment}
            adjustForTimezone={adjustForTimezone}
            />
          )}
          {isModalOpen && selectedAppointment && (
            <AppointmentModal
              appointment={selectedAppointment}
              onClose={handleCloseModal}
              onDelete={handleDeleteAppointment}
              formatDate={formatDate}
              formatTime={formatTime}
            />
          )}
        </div>
      )}

      {activeTab === 'collaborateurs' && (
        <div>
          <h2 className="text-2xl font-bold mb-4">Collaborateurs accessibles</h2>
          {accessibleEmployees.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {accessibleEmployees.map((employee) => (
                <div key={employee.id} className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-green-800">
                      {employee.firstName} {employee.lastName}
                    </h3>
                    <span className="bg-green-200 text-green-800 text-xs px-2 py-1 rounded-full">
                      {employee.distance} km
                    </span>
                  </div>
                  <p className="text-sm text-green-700 mb-2">
                    Zone: {employee.defaultRadius} km
                  </p>
                  {employee.address?.street && (
                    <p className="text-xs text-green-600">
                      📍 {employee.address.street}, {employee.address.postalCode} {employee.address.city}
                    </p>
                  )}
                  {employee.phoneNumber && (
                    <p className="text-xs text-green-600 mt-1">
                      📞 {employee.phoneNumber}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="text-gray-600 text-center">
                {employees.length > 0 ? 
                  "Aucun collaborateur n'a ce client dans sa zone d'intervention" : 
                  "Chargement des collaborateurs..."}
              </p>
            </div>
          )}
        </div>
      )}
    
    </div>
    
    </div>
  );
};

export default ClientDetails;
